/**
 * EngineeringHistory.tsx - Historia obliczeń inżynierskich
 * DARK THEME VERSION
 */

import React, { useState, useEffect } from 'react';
import { Clock, Download, Trash2, Calculator, Eye, ArrowLeft, Database } from 'lucide-react';

interface ProjectData {
  id: string;
  input: any;
  results: any;
  timestamp: string;
}

const EngineeringHistory: React.FC = () => {
  const [projects, setProjects] = useState<ProjectData[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectData | null>(null);

  useEffect(() => {
    // Wczytaj zapisane projekty
    const savedProjects = JSON.parse(localStorage.getItem('engineeringProjects') || '[]');
    setProjects(savedProjects.reverse()); // Najnowsze na górze
  }, []);

  const deleteProject = (id: string) => {
    if (confirm('Czy na pewno chcesz usunąć ten projekt?')) {
      const updatedProjects = projects.filter(p => p.id !== id);
      setProjects(updatedProjects);
      localStorage.setItem('engineeringProjects', JSON.stringify(updatedProjects.reverse()));
    }
  };

  const loadProject = (project: ProjectData) => {
    // Przekaż dane do strony głównej
    localStorage.setItem('engineeringLoadProject', JSON.stringify(project));
    window.location.href = '/engineering';
  };

  const formatDate = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('pl-PL');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-sm shadow-xl border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <a 
                href="/engineering"
                className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-white" />
              </a>
              <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg shadow-lg">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                  Historia obliczeń
                  <Database className="w-5 h-5 text-purple-400" />
                </h1>
                <p className="text-sm text-gray-400">{projects.length} zapisanych projektów</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              PlexiSystem v4.0
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl shadow-2xl p-6 border border-gray-700">
          {projects.length === 0 ? (
            <div className="text-center py-12">
              <Calculator className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 text-lg mb-4">Brak zapisanych projektów</p>
              <a 
                href="/engineering" 
                className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
              >
                <Calculator className="w-5 h-5" />
                Przejdź do obliczeń
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              {projects.map(project => {
                const bestMaterial = project.results?.materials?.find((m: any) => m.feasible) || project.results?.materials?.[0];
                
                return (
                  <div 
                    key={project.id} 
                    className="bg-gray-700/30 border border-gray-600 rounded-lg p-4 hover:bg-gray-700/50 transition-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-2">
                          <span className="font-semibold text-lg text-white">{project.id}</span>
                          <span className="text-sm text-gray-500">{formatDate(project.timestamp)}</span>
                        </div>
                        
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <span className="text-gray-500">Typ:</span>
                            <span className="ml-2 font-medium text-gray-300">
                              {project.input.type === 'box' ? 'Pojemnik' : 'Tuba'}
                            </span>
                          </div>
                          
                          <div>
                            <span className="text-gray-500">Wymiary:</span>
                            <span className="ml-2 font-medium text-gray-300">
                              {project.input.type === 'box' 
                                ? `${(project.input.dimensions.a * 1000).toFixed(0)}×${(project.input.dimensions.b * 1000).toFixed(0)}×${(project.input.dimensions.h * 1000).toFixed(0)}mm`
                                : `Ø${(project.input.dimensions.r * 2000).toFixed(0)}×${(project.input.dimensions.h * 1000).toFixed(0)}mm`
                              }
                            </span>
                          </div>
                          
                          {bestMaterial && (
                            <>
                              <div>
                                <span className="text-gray-500">Materiał:</span>
                                <span className="ml-2 font-medium text-purple-400">{bestMaterial.name}</span>
                              </div>
                              
                              <div>
                                <span className="text-gray-500">Grubość:</span>
                                <span className="ml-2 font-medium text-green-400">{bestMaterial.t_req_mm.toFixed(1)} mm</span>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 ml-4">
                        <button
                          onClick={() => setSelectedProject(project)}
                          className="p-2 text-blue-400 hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="Podgląd"
                        >
                          <Eye className="w-5 h-5" />
                        </button>
                        
                        <button
                          onClick={() => loadProject(project)}
                          className="p-2 text-green-400 hover:bg-green-900/30 rounded-lg transition-colors"
                          title="Wczytaj"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                        
                        <button
                          onClick={() => deleteProject(project.id)}
                          className="p-2 text-red-400 hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Usuń"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal podglądu */}
        {selectedProject && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50">
            <div className="bg-gray-800 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-auto p-6 border border-gray-700 shadow-2xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Podgląd projektu {selectedProject.id}</h3>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="text-gray-400 hover:text-white transition-colors text-2xl"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-semibold mb-2 text-purple-400">Parametry wejściowe:</h4>
                  <pre className="bg-gray-900/50 p-3 rounded-lg text-sm overflow-x-auto text-gray-300 border border-gray-700">
                    {JSON.stringify(selectedProject.input, null, 2)}
                  </pre>
                </div>
                
                <div>
                  <h4 className="font-semibold mb-2 text-purple-400">Wyniki obliczeń:</h4>
                  <pre className="bg-gray-900/50 p-3 rounded-lg text-sm overflow-x-auto text-gray-300 border border-gray-700">
                    {JSON.stringify(selectedProject.results, null, 2)}
                  </pre>
                </div>
              </div>
              
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => loadProject(selectedProject)}
                  className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all"
                >
                  Wczytaj do edycji
                </button>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Zamknij
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EngineeringHistory;